import React, { useState, useMemo } from 'react';
import {
  Student,
  Teacher,
  SchoolSettings,
  HomeroomJournalNote,
  HomeroomJournalCategory,
  HomeroomJournalStatus,
} from '../../types.ts';
import {
  BookOpen,
  Plus,
  Search,
  Filter,
  Calendar,
  User,
  Tag,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Printer,
  Trash2,
  Edit3,
  ChevronDown,
  X,
  Send,
  AlertTriangle,
  Award,
  Sparkles,
  Layers,
  PhoneCall,
  UserCheck,
  ArrowRight,
  MessageSquare,
  ShieldAlert,
  CheckSquare,
} from 'lucide-react';

const safeFormatDate = (dateStr: string | null | undefined, options?: Intl.DateTimeFormatOptions, fallback: string = '-') => {
  if (!dateStr) return fallback;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('id-ID', options);
};

interface HomeroomJournalManagerProps {
  homeroomClass: string;
  students: Student[];
  activeUser: Teacher;
  schoolSettings: SchoolSettings;
  notes?: HomeroomJournalNote[];
  onSaveNote?: (note: HomeroomJournalNote, method: 'POST' | 'PUT' | 'DELETE') => void | Promise<any>;
  appTheme?: string;
}

const CATEGORIES: HomeroomJournalCategory[] = [
  'Akademik',
  'Kehadiran',
  'Kedisiplinan',
  'Karakter',
  'Sosial',
  'Kebersihan',
  'Tanggung jawab',
  'Prestasi',
  'Pelanggaran',
  'Lainnya',
];

const STATUS_LIST: HomeroomJournalStatus[] = [
  'Belum Diproses',
  'Dalam Proses',
  'Selesai',
  'Perlu Panggilan Ortu',
  'Perlu Konseling BK',
];

const CATEGORY_THEMES: Record<HomeroomJournalCategory, { bg: string; text: string; badge: string; icon: string }> = {
  Akademik: {
    bg: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-300',
    badge: 'bg-blue-100 dark:bg-blue-950/80 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    icon: '📚',
  },
  Kehadiran: {
    bg: 'bg-cyan-500',
    text: 'text-cyan-700 dark:text-cyan-300',
    badge: 'bg-cyan-100 dark:bg-cyan-950/80 text-cyan-800 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800',
    icon: '🗓️',
  },
  Kedisiplinan: {
    bg: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    icon: '⏰',
  },
  Karakter: {
    bg: 'bg-purple-500',
    text: 'text-purple-700 dark:text-purple-300',
    badge: 'bg-purple-100 dark:bg-purple-950/80 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    icon: '💎',
  },
  Sosial: {
    bg: 'bg-teal-500',
    text: 'text-teal-700 dark:text-teal-300',
    badge: 'bg-teal-100 dark:bg-teal-950/80 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-800',
    icon: '🤝',
  },
  Kebersihan: {
    bg: 'bg-emerald-500',
    text: 'text-emerald-700 dark:text-emerald-300',
    badge: 'bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    icon: '🧹',
  },
  'Tanggung jawab': {
    bg: 'bg-indigo-500',
    text: 'text-indigo-700 dark:text-indigo-300',
    badge: 'bg-indigo-100 dark:bg-indigo-950/80 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
    icon: '🎯',
  },
  Prestasi: {
    bg: 'bg-yellow-500',
    text: 'text-yellow-700 dark:text-yellow-300',
    badge: 'bg-yellow-100 dark:bg-yellow-950/80 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
    icon: '🏆',
  },
  Pelanggaran: {
    bg: 'bg-rose-500',
    text: 'text-rose-700 dark:text-rose-300',
    badge: 'bg-rose-100 dark:bg-rose-950/80 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800',
    icon: '⚠️',
  },
  Lainnya: {
    bg: 'bg-slate-500',
    text: 'text-slate-700 dark:text-slate-300',
    badge: 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    icon: '📌',
  },
};

const STATUS_THEMES: Record<HomeroomJournalStatus, { badge: string; icon: React.ReactNode }> = {
  Selesai: {
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800',
    icon: <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400" />,
  },
  'Dalam Proses': {
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-950/80 dark:text-blue-300 border-blue-300 dark:border-blue-800',
    icon: <Clock size={14} className="text-blue-600 dark:text-blue-400" />,
  },
  'Belum Diproses': {
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700',
    icon: <AlertCircle size={14} className="text-slate-500" />,
  },
  'Perlu Panggilan Ortu': {
    badge: 'bg-rose-100 text-rose-800 dark:bg-rose-950/80 dark:text-rose-300 border-rose-300 dark:border-rose-800 font-bold',
    icon: <PhoneCall size={14} className="text-rose-600 dark:text-rose-400" />,
  },
  'Perlu Konseling BK': {
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-950/80 dark:text-purple-300 border-purple-300 dark:border-purple-800 font-bold',
    icon: <ShieldAlert size={14} className="text-purple-600 dark:text-purple-400" />,
  },
};

const QUICK_TEMPLATES = [
  {
    title: 'Tugas Belum Dikumpulkan',
    category: 'Akademik' as HomeroomJournalCategory,
    description: 'Peserta didik belum mengumpulkan tugas mata pelajaran.',
    actionTaken: 'Diberikan pengingat dan batas waktu tambahan untuk mengumpulkan tugas.',
    status: 'Dalam Proses' as HomeroomJournalStatus,
  },
  {
    title: 'Terlambat Masuk Sekolah',
    category: 'Kedisiplinan' as HomeroomJournalCategory,
    description: 'Peserta didik datang terlambat masuk kelas saat jam pertama.',
    actionTaken: 'Memberikan pembinaan kedisiplinan dan mencatat jam kedatangan.',
    status: 'Selesai' as HomeroomJournalStatus,
  },
  {
    title: 'Pencapaian Prestasi',
    category: 'Prestasi' as HomeroomJournalCategory,
    description: 'Peserta didik berhasil meraih juara / apresiasi dalam kegiatan lomba.',
    actionTaken: 'Memberikan apresiasi di depan kelas dan menyampaikan kabar baik ke orang tua.',
    status: 'Selesai' as HomeroomJournalStatus,
  },
  {
    title: 'Pembinaan Sikap & Karakter',
    category: 'Karakter' as HomeroomJournalCategory,
    description: 'Perlu bimbingan dan perhatian khusus terkait perilaku dan hubungan antar sesama siswa.',
    actionTaken: 'Mengajak dialog secara pribadi dari hati ke hati saat jam istirahat.',
    status: 'Dalam Proses' as HomeroomJournalStatus,
  },
];

const HomeroomJournalManager: React.FC<HomeroomJournalManagerProps> = ({
  homeroomClass,
  students = [],
  activeUser,
  schoolSettings,
  notes = [],
  onSaveNote,
}) => {
  // Filter students for homeroom class
  const classStudents = useMemo(() => {
    return students.filter((s) => s.class === homeroomClass);
  }, [students, homeroomClass]);

  // Filter notes belonging to this homeroom class
  const classNotes = useMemo(() => {
    return notes
      .filter((n) => n.homeroomClass === homeroomClass)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [notes, homeroomClass]);

  // Filters & State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudentFilter, setSelectedStudentFilter] = useState<string>('ALL');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('ALL');
  const [viewLayout, setViewLayout] = useState<'timeline' | 'table'>('timeline');

  // Modal State for Add / Edit Note
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<HomeroomJournalNote | null>(null);

  const todayStr = new Date().toISOString().split('T')[0];

  // Form Fields
  const [formDate, setFormDate] = useState<string>(todayStr);
  const [formStudentId, setFormStudentId] = useState<string>('');
  const [formStudentName, setFormStudentName] = useState<string>('Seluruh Kelas / Umum');
  const [formCategory, setFormCategory] = useState<HomeroomJournalCategory>('Akademik');
  const [formDescription, setFormDescription] = useState<string>('');
  const [formActionTaken, setFormActionTaken] = useState<string>('');
  const [formResult, setFormResult] = useState<string>('');
  const [formStatus, setFormStatus] = useState<HomeroomJournalStatus>('Selesai');
  const [formNextNotes, setFormNextNotes] = useState<string>('');

  // Open Modal to Create New Note
  const handleOpenAddModal = (studentId?: string) => {
    setEditingNote(null);
    setFormDate(todayStr);

    if (studentId) {
      const std = classStudents.find((s) => s.id === studentId);
      setFormStudentId(studentId);
      setFormStudentName(std?.name || '');
    } else {
      setFormStudentId('');
      setFormStudentName('Seluruh Kelas / Umum');
    }

    setFormCategory('Akademik');
    setFormDescription('');
    setFormActionTaken('');
    setFormResult('');
    setFormStatus('Selesai');
    setFormNextNotes('');
    setIsModalOpen(true);
  };

  // Open Modal to Edit Note
  const handleOpenEditModal = (note: HomeroomJournalNote) => {
    setEditingNote(note);
    setFormDate(note.date || todayStr);
    setFormStudentId(note.studentId || '');
    setFormStudentName(note.studentName || 'Seluruh Kelas / Umum');
    setFormCategory(note.category || 'Akademik');
    setFormDescription(note.description || '');
    setFormActionTaken(note.actionTaken || '');
    setFormResult(note.result || '');
    setFormStatus(note.status || 'Selesai');
    setFormNextNotes(note.nextNotes || '');
    setIsModalOpen(true);
  };

  // Apply Quick Template
  const handleApplyTemplate = (tpl: typeof QUICK_TEMPLATES[0]) => {
    setFormCategory(tpl.category);
    if (!formDescription) setFormDescription(tpl.description);
    if (!formActionTaken) setFormActionTaken(tpl.actionTaken);
    setFormStatus(tpl.status);
  };

  // Student dropdown change handler
  const handleStudentSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setFormStudentId(val);
    if (!val) {
      setFormStudentName('Seluruh Kelas / Umum');
    } else {
      const found = classStudents.find((s) => s.id === val);
      setFormStudentName(found?.name || '');
    }
  };

  // Handle Form Submit
  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!onSaveNote) return;

    const payload: HomeroomJournalNote = {
      id: editingNote ? editingNote.id : `hj_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      date: formDate,
      homeroomClass,
      studentId: formStudentId || undefined,
      studentName: formStudentName || 'Seluruh Kelas / Umum',
      category: formCategory,
      description: formDescription,
      actionTaken: formActionTaken,
      result: formResult,
      status: formStatus,
      nextNotes: formNextNotes,
      teacherId: activeUser?.id,
      teacherName: activeUser?.name,
      createdAt: editingNote?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    onSaveNote(payload, editingNote ? 'PUT' : 'POST');
    setIsModalOpen(false);
  };

  // Handle Quick Status Change from card
  const handleQuickStatusChange = (note: HomeroomJournalNote, newStatus: HomeroomJournalStatus) => {
    if (!onSaveNote) return;
    const updated = {
      ...note,
      status: newStatus,
      updatedAt: new Date().toISOString(),
    };
    onSaveNote(updated, 'PUT');
  };

  // Handle Delete Note
  const handleDelete = (note: HomeroomJournalNote) => {
    if (!onSaveNote) return;
    if (confirm(`Apakah Anda yakin ingin menghapus catatan jurnal tanggal ${note.date} untuk ${note.studentName}?`)) {
      onSaveNote(note, 'DELETE');
    }
  };

  // Filtered Notes List
  const filteredNotes = useMemo(() => {
    return classNotes.filter((n) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        n.studentName.toLowerCase().includes(q) ||
        n.description.toLowerCase().includes(q) ||
        n.actionTaken.toLowerCase().includes(q) ||
        (n.result && n.result.toLowerCase().includes(q)) ||
        (n.nextNotes && n.nextNotes.toLowerCase().includes(q));

      const matchesStudent =
        selectedStudentFilter === 'ALL' ||
        (selectedStudentFilter === 'GENERAL' && !n.studentId) ||
        n.studentId === selectedStudentFilter;

      const matchesCategory =
        selectedCategoryFilter === 'ALL' || n.category === selectedCategoryFilter;

      const matchesStatus =
        selectedStatusFilter === 'ALL' || n.status === selectedStatusFilter;

      return matchesSearch && matchesStudent && matchesCategory && matchesStatus;
    });
  }, [classNotes, searchQuery, selectedStudentFilter, selectedCategoryFilter, selectedStatusFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = classNotes.length;
    const selesai = classNotes.filter((n) => n.status === 'Selesai').length;
    const dalamProses = classNotes.filter((n) => n.status === 'Dalam Proses').length;
    const belumDiproses = classNotes.filter((n) => n.status === 'Belum Diproses').length;
    const ortuOrBk = classNotes.filter(
      (n) => n.status === 'Perlu Panggilan Ortu' || n.status === 'Perlu Konseling BK'
    ).length;

    return { total, selesai, dalamProses, belumDiproses, ortuOrBk };
  }, [classNotes]);

  // Print Official Homeroom Journal Document
  const handlePrintJournal = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    const schoolTitle = schoolSettings?.schoolName || 'SMP NEGERI 1';

    const rowsHtml = filteredNotes
      .map((n, idx) => {
        return `
          <tr>
            <td style="text-align: center; vertical-align: top;">${idx + 1}</td>
            <td style="text-align: center; vertical-align: top; font-weight: bold; white-space: nowrap;">
              ${safeFormatDate(n.date, { day: 'numeric', month: 'short', year: 'numeric' })}
            </td>
            <td style="vertical-align: top;">
              <strong>${n.studentName}</strong>
            </td>
            <td style="text-align: center; vertical-align: top;">
              <span style="font-size: 8.5pt; font-weight: bold; padding: 2px 5px; border: 1px solid #ccc; border-radius: 4px;">
                ${n.category}
              </span>
            </td>
            <td style="vertical-align: top;">
              ${n.description}
            </td>
            <td style="vertical-align: top;">
              ${n.actionTaken || '-'}
            </td>
            <td style="vertical-align: top;">
              ${n.result || '-'}
            </td>
            <td style="text-align: center; vertical-align: top; font-weight: bold;">
              ${n.status}
            </td>
            <td style="vertical-align: top; font-size: 8.5pt;">
              ${n.nextNotes || '-'}
            </td>
          </tr>
        `;
      })
      .join('');

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Jurnal Catatan Wali Kelas ${homeroomClass}</title>
          <style>
            @page { size: A4 landscape; margin: 12mm; }
            body { font-family: 'Times New Roman', Times, serif; color: #000; font-size: 9.5pt; line-height: 1.3; margin: 0; padding: 0; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 12px; }
            .header h1 { font-size: 14pt; margin: 0; text-transform: uppercase; }
            .header h2 { font-size: 12pt; margin: 3px 0 0 0; text-transform: uppercase; }
            .header p { font-size: 9.5pt; margin: 2px 0 0 0; }
            .info-table { width: 100%; margin-bottom: 10px; font-size: 9.5pt; }
            table.data { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 9pt; }
            table.data th, table.data td { border: 1px solid #000; padding: 5px; }
            table.data th { background-color: #f2f2f2; text-align: center; font-weight: bold; }
            .footer-sig { margin-top: 30px; width: 100%; font-size: 9.5pt; }
            .footer-sig td { border: none; text-align: center; vertical-align: top; }
            .sig-space { height: 50px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${schoolTitle.toUpperCase()}</h1>
            <h2>JURNAL DIGITAL CATATAN WALI KELAS ${homeroomClass}</h2>
            <p>Tahun Ajaran: ${schoolSettings?.academicYear || '2025/2026'} - Semester: ${schoolSettings?.semester || 'Ganjil'}</p>
          </div>

          <table class="info-table">
            <tr>
              <td width="50%"><strong>Kelas Perwalian:</strong> ${homeroomClass} (${classStudents.length} Peserta Didik)</td>
              <td width="50%" style="text-align: right;"><strong>Wali Kelas:</strong> ${activeUser?.name} (NIP. ${activeUser?.nip || '-'})</td>
            </tr>
          </table>

          <table class="data">
            <thead>
              <tr>
                <th width="3%">NO</th>
                <th width="10%">TANGGAL</th>
                <th width="16%">NAMA SISWA</th>
                <th width="9%">KATEGORI</th>
                <th width="20%">URAIAN KEJADIAN</th>
                <th width="16%">TINDAKAN DILAKUKAN</th>
                <th width="12%">HASIL</th>
                <th width="8%">STATUS</th>
                <th width="6%">CATATAN LANJUT</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml || '<tr><td colSpan="9" style="text-align: center;">Belum ada catatan jurnal recorded.</td></tr>'}
            </tbody>
          </table>

          <table class="footer-sig">
            <tr>
              <td width="50%">
                Mengetahui,<br/>
                Kepala Sekolah
                <div class="sig-space"></div>
                <strong>${schoolSettings?.principalName || '................................'}</strong><br/>
                NIP. ${schoolSettings?.principalNip || '................................'}
              </td>
              <td width="50%">
                ${schoolSettings?.address ? schoolSettings.address.split(',')[0] : 'Sumedang'}, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                Wali Kelas ${homeroomClass}
                <div class="sig-space"></div>
                <strong>${activeUser?.name}</strong><br/>
                NIP. ${activeUser?.nip || '................................'}
              </td>
            </tr>
          </table>

          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `;

    printWin.document.write(content);
    printWin.document.close();
  };

  return (
    <div className="space-y-6">
      {/* Banner / Header Title */}
      <div className="bg-gradient-to-r from-emerald-800 via-emerald-700 to-teal-800 text-white rounded-2xl p-6 shadow-md relative overflow-hidden">
        <div className="absolute right-0 top-0 translate-x-4 -translate-y-4 opacity-10 pointer-events-none">
          <BookOpen size={200} />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-black tracking-wider uppercase">
                📖 Jurnal Digital Wali Kelas
              </span>
              <span className="px-2.5 py-0.5 bg-amber-400 text-slate-900 rounded-full text-xs font-black">
                Kelas {homeroomClass}
              </span>
            </div>
            <h2 className="text-xl md:text-2xl font-black">Catatan Bimbingan & Kejadian Peserta Didik</h2>
            <p className="text-xs text-emerald-100 max-w-2xl leading-relaxed">
              Catat perkembangan akademik, kedisiplinan, karakter, apresiasi prestasi, hingga penanganan khusus siswa secara rapi, berkesinambungan, dan terstruktur.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => handleOpenAddModal()}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold text-xs rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5 cursor-pointer"
            >
              <Plus size={18} />
              <span>Tambah Catatan Baru</span>
            </button>

            <button
              onClick={handlePrintJournal}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl backdrop-blur-sm border border-white/20 shadow transition-all cursor-pointer"
            >
              <Printer size={18} />
              <span>Cetak Jurnal</span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3.5">
        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-xs flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">Total Catatan</div>
            <div className="text-2xl font-black text-slate-800 dark:text-white mt-1">{stats.total}</div>
          </div>
          <div className="p-2.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl">
            <BookOpen size={20} />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-emerald-200 dark:border-emerald-900/50 shadow-xs flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase text-emerald-600 dark:text-emerald-400">Selesai</div>
            <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">{stats.selesai}</div>
          </div>
          <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-xl">
            <CheckCircle2 size={20} />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-blue-200 dark:border-blue-900/50 shadow-xs flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase text-blue-600 dark:text-blue-400">Dalam Proses</div>
            <div className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">{stats.dalamProses}</div>
          </div>
          <div className="p-2.5 bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 rounded-xl">
            <Clock size={20} />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-amber-200 dark:border-amber-900/50 shadow-xs flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase text-amber-600 dark:text-amber-400">Belum Diproses</div>
            <div className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">{stats.belumDiproses}</div>
          </div>
          <div className="p-2.5 bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 rounded-xl">
            <AlertCircle size={20} />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-rose-200 dark:border-rose-900/50 shadow-xs flex items-center justify-between col-span-2 sm:col-span-1">
          <div>
            <div className="text-[11px] font-bold uppercase text-rose-600 dark:text-rose-400">Ortu / BK</div>
            <div className="text-2xl font-black text-rose-600 dark:text-rose-400 mt-1">{stats.ortuOrBk}</div>
          </div>
          <div className="p-2.5 bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 rounded-xl">
            <ShieldAlert size={20} />
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-xs space-y-3">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          {/* Search box */}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari uraian kejadian, nama siswa, tindakan, atau catatan..."
              className="w-full pl-10 pr-4 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {/* View layout toggle */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-900/80 p-1 rounded-xl border border-slate-200 dark:border-slate-700 shrink-0">
            <button
              onClick={() => setViewLayout('timeline')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                viewLayout === 'timeline'
                  ? 'bg-emerald-600 text-white shadow'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              📑 Tampilan Jurnal
            </button>
            <button
              onClick={() => setViewLayout('table')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                viewLayout === 'table'
                  ? 'bg-emerald-600 text-white shadow'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              📊 Tabel Lengkap
            </button>
          </div>
        </div>

        {/* Dropdown Filters */}
        <div className="flex flex-wrap items-center gap-2.5 pt-1 border-t border-slate-100 dark:border-slate-700/60">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 shrink-0">
            <Filter size={14} />
            <span>Filter:</span>
          </div>

          {/* Student Filter */}
          <select
            value={selectedStudentFilter}
            onChange={(e) => setSelectedStudentFilter(e.target.value)}
            className="px-3 py-1.5 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="ALL">👤 Semua Peserta Didik</option>
            <option value="GENERAL">🏫 Seluruh Kelas / Umum</option>
            {classStudents.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.nis || '-'})
              </option>
            ))}
          </select>

          {/* Category Filter */}
          <select
            value={selectedCategoryFilter}
            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            className="px-3 py-1.5 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="ALL">🏷️ Semua Kategori</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_THEMES[cat]?.icon} {cat}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatusFilter}
            onChange={(e) => setSelectedStatusFilter(e.target.value)}
            className="px-3 py-1.5 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="ALL">📌 Semua Status</option>
            {STATUS_LIST.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>

          {(searchQuery || selectedStudentFilter !== 'ALL' || selectedCategoryFilter !== 'ALL' || selectedStatusFilter !== 'ALL') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedStudentFilter('ALL');
                setSelectedCategoryFilter('ALL');
                setSelectedStatusFilter('ALL');
              }}
              className="px-2.5 py-1.5 text-xs font-bold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/50 hover:bg-rose-100 rounded-xl transition-all"
            >
              Reset Filter
            </button>
          )}

          <div className="ml-auto text-xs font-bold text-slate-400">
            Menampilkan {filteredNotes.length} dari {classNotes.length} catatan
          </div>
        </div>
      </div>

      {/* LIST CONTENT AREA */}
      {filteredNotes.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-12 border border-slate-200 dark:border-slate-700/80 text-center space-y-3">
          <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto">
            <BookOpen size={32} />
          </div>
          <h3 className="font-bold text-slate-800 dark:text-white text-base">
            Belum Ada Catatan Jurnal
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            Belum ada entri catatan perwalian untuk Kelas {homeroomClass} yang sesuai dengan filter. Klik tombol &quot;Tambah Catatan Baru&quot; di atas untuk mulai mencatat.
          </p>
          <button
            onClick={() => handleOpenAddModal()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow transition-all cursor-pointer"
          >
            <Plus size={16} />
            <span>Buat Catatan Pertama</span>
          </button>
        </div>
      ) : viewLayout === 'timeline' ? (
        /* TIMELINE / JOURNAL CARDS VIEW */
        <div className="space-y-4">
          {filteredNotes.map((note) => {
            const catTheme = CATEGORY_THEMES[note.category] || CATEGORY_THEMES['Lainnya'];
            const statusTheme = STATUS_THEMES[note.status] || STATUS_THEMES['Selesai'];

            return (
              <div
                key={note.id}
                className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-xs hover:shadow-md transition-all relative overflow-hidden group"
              >
                {/* Colored Top Border Indicator */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 ${catTheme.bg}`} />

                <div className="space-y-3">
                  {/* Header Row: Date + Category Badge + Student Name + Status Badge */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-700/60 pb-3">
                    <div className="flex flex-wrap items-center gap-2.5">
                      {/* Date */}
                      <span className="flex items-center gap-1.5 text-xs font-extrabold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-900/80 px-3 py-1 rounded-lg">
                        <Calendar size={14} className="text-emerald-600 dark:text-emerald-400" />
                        {safeFormatDate(note.date, {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>

                      {/* Category Badge */}
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border ${catTheme.badge}`}>
                        <span>{catTheme.icon}</span>
                        <span>{note.category}</span>
                      </span>

                      {/* Student Name */}
                      <span className="flex items-center gap-1.5 text-sm font-black text-slate-900 dark:text-white">
                        <User size={15} className="text-emerald-600 dark:text-emerald-400" />
                        {note.studentName}
                      </span>
                    </div>

                    {/* Status Badge & Actions */}
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusTheme.badge}`}>
                        {statusTheme.icon}
                        <span>{note.status}</span>
                      </span>

                      <button
                        onClick={() => handleOpenEditModal(note)}
                        className="p-1.5 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-all"
                        title="Edit Catatan"
                      >
                        <Edit3 size={15} />
                      </button>

                      <button
                        onClick={() => handleDelete(note)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition-all"
                        title="Hapus Catatan"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  {/* Body Content Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Left Column: Uraian Kejadian */}
                    <div className="bg-slate-50/80 dark:bg-slate-900/50 p-3.5 rounded-xl border border-slate-100 dark:border-slate-700/50 space-y-1">
                      <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                        <MessageSquare size={13} className="text-emerald-600 dark:text-emerald-400" />
                        <span>Uraian Kejadian / Permasalahan:</span>
                      </div>
                      <p className="text-xs font-medium text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-line">
                        {note.description}
                      </p>
                    </div>

                    {/* Right Column: Tindakan yang dilakukan */}
                    <div className="bg-slate-50/80 dark:bg-slate-900/50 p-3.5 rounded-xl border border-slate-100 dark:border-slate-700/50 space-y-1">
                      <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                        <CheckSquare size={13} className="text-emerald-600 dark:text-emerald-400" />
                        <span>Tindakan / Pembinaan yang Dilakukan:</span>
                      </div>
                      <p className="text-xs font-medium text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-line">
                        {note.actionTaken || '-'}
                      </p>
                    </div>
                  </div>

                  {/* Optional Footer Grid: Hasil & Catatan Berikutnya */}
                  {(note.result || note.nextNotes) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                      {note.result && (
                        <div className="p-3 bg-emerald-50/60 dark:bg-emerald-950/30 rounded-xl border border-emerald-100 dark:border-emerald-900/40 text-xs">
                          <span className="font-bold text-emerald-800 dark:text-emerald-300 block mb-0.5">
                            🎯 Hasil / Tanggapan:
                          </span>
                          <span className="text-slate-700 dark:text-slate-300">{note.result}</span>
                        </div>
                      )}

                      {note.nextNotes && (
                        <div className="p-3 bg-amber-50/60 dark:bg-amber-950/30 rounded-xl border border-amber-100 dark:border-amber-900/40 text-xs">
                          <span className="font-bold text-amber-800 dark:text-amber-300 block mb-0.5">
                            📌 Catatan / Rencana Tindak Lanjut Berikutnya:
                          </span>
                          <span className="text-slate-700 dark:text-slate-300">{note.nextNotes}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Quick Action Status Change Pills */}
                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100 dark:border-slate-700/40 text-[11px]">
                    <div className="text-slate-400 flex items-center gap-1">
                      <span>Dicatat oleh Wali Kelas:</span>
                      <strong className="text-slate-600 dark:text-slate-300">{note.teacherName || activeUser?.name}</strong>
                    </div>

                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 font-medium mr-1">Ubah Status Cepat:</span>
                      {STATUS_LIST.map((st) => (
                        <button
                          key={st}
                          onClick={() => handleQuickStatusChange(note, st)}
                          disabled={note.status === st}
                          className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all ${
                            note.status === st
                              ? 'bg-slate-800 text-white dark:bg-white dark:text-slate-900'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-emerald-100 hover:text-emerald-700'
                          }`}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* TABLE VIEW */
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700 dark:text-slate-200">
              <thead className="bg-slate-100 dark:bg-slate-900/80 text-[11px] font-bold uppercase text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="py-3 px-3 text-center w-10">No</th>
                  <th className="py-3 px-3 w-28">Tanggal</th>
                  <th className="py-3 px-3 min-w-[150px]">Nama Peserta Didik</th>
                  <th className="py-3 px-3 w-28 text-center">Kategori</th>
                  <th className="py-3 px-3 min-w-[200px]">Uraian Kejadian</th>
                  <th className="py-3 px-3 min-w-[180px]">Tindakan</th>
                  <th className="py-3 px-3 min-w-[140px]">Hasil</th>
                  <th className="py-3 px-3 text-center w-32">Status</th>
                  <th className="py-3 px-3 text-center w-20">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700/60 bg-white dark:bg-slate-800">
                {filteredNotes.map((note, idx) => {
                  const catTheme = CATEGORY_THEMES[note.category] || CATEGORY_THEMES['Lainnya'];
                  const statusTheme = STATUS_THEMES[note.status] || STATUS_THEMES['Selesai'];

                  return (
                    <tr key={note.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors">
                      <td className="py-3 px-3 text-center font-bold text-slate-500">{idx + 1}</td>
                      <td className="py-3 px-3 font-mono text-slate-600 dark:text-slate-300 font-bold whitespace-nowrap">
                        {note.date}
                      </td>
                      <td className="py-3 px-3 font-bold text-slate-900 dark:text-white">
                        {note.studentName}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${catTheme.badge}`}>
                          {note.category}
                        </span>
                      </td>
                      <td className="py-3 px-3 max-w-xs truncate">{note.description}</td>
                      <td className="py-3 px-3 max-w-xs truncate">{note.actionTaken || '-'}</td>
                      <td className="py-3 px-3 max-w-xs truncate">{note.result || '-'}</td>
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${statusTheme.badge}`}>
                          {note.status}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleOpenEditModal(note)}
                            className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                            title="Edit"
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(note)}
                            className="p-1 text-slate-400 hover:text-rose-600 rounded"
                            title="Hapus"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FORM MODAL: ADD / EDIT JOURNAL NOTE */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-2xl w-full border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden my-8 animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-700 to-teal-700 text-white p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl">
                  <BookOpen size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-base">
                    {editingNote ? 'Edit Catatan Jurnal Wali Kelas' : 'Tambah Catatan Jurnal Wali Kelas'}
                  </h3>
                  <p className="text-xs text-emerald-100">
                    Kelas {homeroomClass} • {schoolSettings?.schoolName || 'SMP Negeri 1'}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-all cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Quick Template Picker */}
            <div className="bg-slate-50 dark:bg-slate-900/80 p-4 border-b border-slate-200 dark:border-slate-700 space-y-2">
              <div className="text-[11px] font-bold text-slate-500 uppercase flex items-center gap-1.5">
                <Sparkles size={13} className="text-amber-500" />
                <span>Templat Cepat Kejadian Umum:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {QUICK_TEMPLATES.map((tpl, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleApplyTemplate(tpl)}
                    className="px-3 py-1.5 bg-white dark:bg-slate-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <span>{CATEGORY_THEMES[tpl.category]?.icon}</span>
                    <span>{tpl.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSubmitForm} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Tanggal */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
                    Tanggal Kejadian <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    className="w-full px-3.5 py-2.5 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {/* Pilih Peserta Didik */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
                    Nama Peserta Didik <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formStudentId}
                    onChange={handleStudentSelectChange}
                    className="w-full px-3.5 py-2.5 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">🏫 Seluruh Kelas / Umum (Kejadian Bersama)</option>
                    {classStudents.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} (NIS: {s.nis || '-'})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Kategori */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
                    Kategori Kejadian <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value as HomeroomJournalCategory)}
                    className="w-full px-3.5 py-2.5 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_THEMES[cat]?.icon} {cat}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Tindak Lanjut */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
                    Status Tindak Lanjut <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as HomeroomJournalStatus)}
                    className="w-full px-3.5 py-2.5 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {STATUS_LIST.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Uraian Kejadian */}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
                  Uraian Kejadian / Detail Masalah <span className="text-rose-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Contoh: Peserta didik belum mengumpulkan tugas Informatika bab 2..."
                  className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Tindakan yang dilakukan */}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
                  Tindakan yang Dilakukan <span className="text-rose-500">*</span>
                </label>
                <textarea
                  required
                  rows={2}
                  value={formActionTaken}
                  onChange={(e) => setFormActionTaken(e.target.value)}
                  placeholder="Contoh: Diberikan pengingat dan kesempatan pengumpulan hingga besok pagi..."
                  className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Hasil */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
                    Hasil / Perkembangan Saat Ini
                  </label>
                  <input
                    type="text"
                    value={formResult}
                    onChange={(e) => setFormResult(e.target.value)}
                    placeholder="Contoh: Siswa sudah paham dan berjanji mengumpulkan..."
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {/* Catatan berikutnya */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1.5">
                    Catatan Berikutnya / Rencana Tindak Lanjut
                  </label>
                  <input
                    type="text"
                    value={formNextNotes}
                    onChange={(e) => setFormNextNotes(e.target.value)}
                    placeholder="Contoh: Pantau ulang pada hari Jumat..."
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-lg transition-all cursor-pointer"
                >
                  <Send size={15} />
                  <span>{editingNote ? 'Simpan Perubahan' : 'Simpan Catatan'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeroomJournalManager;
